import { useEffect } from "react";
import { useLocation, useNavigate, Location } from 'react-router-dom';
import { helpers, Enums } from "@shared";
import { useAllowedPaymentTypes } from "../hooks";
import { PaymentType } from "./shared/enums";
import { ChevronLeftIcon, ChevronRightIcon } from "@icons";
import { IconButton } from "@common";
import BankIllustration from "@illustrations/bank-160x160.png";
import CardsIllustration from "@illustrations/cards-160x160.png";
import type { PaymentTypeFee } from "@app-types";
import { PostHogEvents, capturePosthogEvent } from "@analytics";

interface LocationState {
    account: {
        residentName: string;
        accountPreferences: {
            paymentTypeFees: PaymentTypeFee[];
        };
    };
    hasMultipleAccounts: boolean;
}

const PaymentMethodSelector = () => {
    const navigate = useNavigate(), location = useLocation() as Location<LocationState>;
    const { allowedPaymentTypes } = useAllowedPaymentTypes();

    const {
        state: {
            account: {
                residentName,
                accountPreferences: { paymentTypeFees }
            },
            hasMultipleAccounts
        }
    } = location;

    const selectOption = (selection: number) => {
        capturePosthogEvent(PostHogEvents.ADD_PAYMENT_METHOD_CLICKED, {
            paymentType: selection
        });
        helpers.navigateNext(location, navigate, undefined, { selectedPaymentType: selection });
    };

    const goBack = () => {
        navigate(-1);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const achFee = paymentTypeFees.find(p => p.idPaymentType === Enums.PaymentType.ACH);
    const ccFee = paymentTypeFees.find(p => p.idPaymentType === Enums.PaymentType.Card);

    const showCreditCardOption = allowedPaymentTypes.includes(PaymentType.Card);
    const showACHOption = allowedPaymentTypes.includes(PaymentType.ACH);

    const achOption = achFee && showACHOption && (
        <button
            key="ach"
            onClick={() => selectOption(Enums.PaymentType.ACH)}
            className="flex items-center gap-[0.8rem] p-[16px] rounded-lg border border-neutral-100 bg-white hover:border-neutral-200 transition-colors focus-visible:outline-focus-ring text-left md:min-w-[536px]"
        >
            <img
                src={BankIllustration}
                alt="Bank Account"
                className="w-[80px] h-[80px] flex-shrink-0"
            />
            <div className="flex-1">
                <span className="block text-[16px] leading-6 font-medium text-neutral-950 tracking-[0.16px]">
                    Bank Account
                </span>
                {achFee.fee > 0 && (
                    <span className="block text-[14px] leading-5 text-neutral-800 tracking-[0.14px]">
                        Will incur a {helpers.printFeeRule(achFee)} ACH processing fee
                    </span>
                )}
            </div>
            <div className="p-1.5 rounded-lg">
                <ChevronRightIcon className="w-5 h-5 text-neutral-950 flex-shrink-0" />
            </div>
        </button>
    );

    const ccOption = ccFee && showCreditCardOption && (
        <button
            key="cc"
            onClick={() => selectOption(Enums.PaymentType.Card)}
            className="flex items-center gap-[0.8rem] p-[16px] rounded-lg border border-neutral-100 bg-white hover:border-neutral-200 transition-colors focus-visible:outline-focus-ring text-left md:min-w-[536px]"
        >
            <img
                src={CardsIllustration}
                alt="Credit or Debit Card"
                className="w-[80px] h-[80px] flex-shrink-0"
            />
            <div className="flex-1">
                <span className="block text-[16px] leading-6 font-medium text-neutral-950 tracking-[0.16px]">
                    Credit or Debit Card
                </span>
                {ccFee.fee > 0 && (
                    <span className="block text-[14px] leading-5 text-neutral-800 tracking-[0.14px]">
                        Will incur a {helpers.printFeeRule(ccFee)} processing fee
                    </span>
                )}
            </div>
            <div className="p-1.5 rounded-lg">
                <ChevronRightIcon className="w-5 h-5 text-neutral-950 flex-shrink-0" />
            </div>
        </button>
    );

    return (
        <div className="flex flex-col w-full">
            <div className="border-b border-neutral-100 pb-6 mb-8">
                <div className="flex items-center gap-2">
                    <IconButton
                        variant="tertiary"
                        size="sm"
                        className="md:btn-icon-lg"
                        onClick={goBack}
                        aria-label="Go back"
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">
                        Add Payment Method
                    </h1>
                </div>
            </div>

            <div className="flex flex-col gap-[0.8rem]">
                <div className="flex flex-col gap-[8px]">
                    {hasMultipleAccounts && (
                        <p className="text-[16px] leading-[24px] font-normal text-neutral-950 tracking-[0.16px]">
                            For {residentName}
                        </p>
                    )}
                    <p className="text-[14px] leading-5 text-neutral-800 tracking-[0.14px]">
                        Select an option below to use for online payments.
                    </p>
                </div>
                {achOption}
                {ccOption}
            </div>
        </div>
    );
};

export default PaymentMethodSelector;
